import XLSX from 'xlsx';

/*
 * Copy and Excel export for charts and tables. Tables are passed around as
 * rows of cells (arrays); numbers stay numbers so Excel can calculate with them.
 */

/** American → en-US, European → da-DK, as in the formatter. */
const localeFromNotation = (notation) => {
    if (notation === 'American') return 'en-US';
    if (notation === 'European') return 'da-DK';
    return undefined;
};

/**
 * Rows as tab-separated text for pasting into Excel or an email. Numbers use
 * the organization's decimal mark without thousand separators, so Excel in
 * that locale reads them as numbers.
 */
export const rowsToTsv = (rows, notation) => {
    const nf = new Intl.NumberFormat(localeFromNotation(notation), { useGrouping: false, maximumFractionDigits: 6 });
    const cell = (v) => {
        if (v == null) return '';
        if (typeof v === 'number') return Number.isFinite(v) ? nf.format(v) : '';
        return String(v).replace(/[\t\r\n]+/g, ' ');
    };
    return rows.map((row) => row.map(cell).join('\t')).join('\n');
};

/** Copies text to the clipboard; resolves false when the browser refuses. */
export const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

/** Excel sheet names: at most 31 characters, none of \ / ? * [ ] : */
const sheetName = (name, used) => {
    let base = String(name).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet';
    let candidate = base;
    for (let i = 2; used.has(candidate); i++) candidate = `${base.slice(0, 28)} ${i}`;
    used.add(candidate);
    return candidate;
};

/** Downloads a workbook: sheets = [{ name, rows }]. */
export const downloadWorkbook = (filename, sheets) => {
    const workbook = XLSX.utils.book_new();
    const used = new Set();
    for (const sheet of sheets) {
        const ws = XLSX.utils.aoa_to_sheet(
            sheet.rows.map((row) => row.map((v) => (typeof v === 'number' && !Number.isFinite(v) ? null : v)))
        );
        // Wide enough first column for account names.
        ws['!cols'] = [{ wch: 36 }];
        XLSX.utils.book_append_sheet(workbook, ws, sheetName(sheet.name, used));
    }
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
};

/** A file name from parts, e.g. "Revenue by year 2026-10-13". */
export const exportFileName = (...parts) =>
    [...parts, new Date().toISOString().slice(0, 10)]
        .filter(Boolean)
        .join(' ')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
